// Push / in-app broadcasts — writes the collections the Flutter inbox reads.
import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Card, TextField, Select, MenuItem, FormControl,
    InputLabel, Alert, CircularProgress, Grid
} from '@mui/material';
import { collection, getDocs, limit, query } from 'firebase/firestore';
import { db, FUNCTIONS_URL } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

const Broadcasts = () => {
    const { getIdToken } = useAuth();
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [type, setType] = useState('announcement');
    const [priority, setPriority] = useState('normal');
    const [audience, setAudience] = useState('all');
    const [sending, setSending] = useState(false);
    const [alert, setAlert] = useState(null);
    const [recent, setRecent] = useState([]);

    const loadRecent = async () => {
        try {
            const snap = await getDocs(query(collection(db, 'broadcast_notifications'), limit(20)));
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            rows.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setRecent(rows.slice(0, 10));
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => { loadRecent(); }, []);

    const send = async () => {
        if (!title.trim() || !message.trim()) {
            setAlert({ type: 'error', text: 'Title and message are required' });
            return;
        }
        setSending(true);
        try {
            const token = await getIdToken();
            const res = await fetch(`${FUNCTIONS_URL}/sendBroadcastNotification`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    title: title.trim(),
                    message: message.trim(),
                    type,
                    priority,
                    targetAudience: audience,
                }),
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(payload.message || payload.error || `Send failed (${res.status})`);
            }
            setAlert({
                type: 'success',
                text: `Queued for ${payload.recipientCount ?? 0} users`,
            });
            setTitle('');
            setMessage('');
            loadRecent();
        } catch (e) {
            console.error(e);
            setAlert({ type: 'error', text: e.message || 'Failed to send broadcast' });
        } finally {
            setSending(false);
        }
    };

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>Broadcasts</Typography>
            {alert && (
                <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mb: 2 }}>
                    {alert.text}
                </Alert>
            )}
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 7 }}>
                    <Card sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: { xs: '100%', md: 960 } }}>
                        <Typography variant="h6">Send notification</Typography>
                        <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
                        <TextField label="Message" value={message} onChange={(e) => setMessage(e.target.value)} multiline minRows={4} />
                        <FormControl>
                            <InputLabel>Type</InputLabel>
                            <Select label="Type" value={type} onChange={(e) => setType(e.target.value)}>
                                <MenuItem value="announcement">Announcement</MenuItem>
                                <MenuItem value="feature">Feature</MenuItem>
                                <MenuItem value="maintenance">Maintenance</MenuItem>
                                <MenuItem value="promotion">Promotion</MenuItem>
                                <MenuItem value="warning">Warning</MenuItem>
                                <MenuItem value="info">Info</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl>
                            <InputLabel>Priority</InputLabel>
                            <Select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)}>
                                <MenuItem value="low">Low</MenuItem>
                                <MenuItem value="normal">Normal</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                                <MenuItem value="urgent">Urgent</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl>
                            <InputLabel>Audience</InputLabel>
                            <Select label="Audience" value={audience} onChange={(e) => setAudience(e.target.value)}>
                                <MenuItem value="all">All users</MenuItem>
                                <MenuItem value="premium">Premium</MenuItem>
                                <MenuItem value="free">Free</MenuItem>
                            </Select>
                        </FormControl>
                        <Button variant="contained" onClick={send} disabled={sending}>
                            {sending ? <CircularProgress size={22} /> : 'Send'}
                        </Button>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, lg: 5 }}>
                    <Card sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Recent sends</Typography>
                        {recent.map((row) => (
                            <Box key={row.id} sx={{ mb: 2 }}>
                                <Typography fontWeight={600}>{row.title}</Typography>
                                <Typography variant="body2" color="text.secondary">{row.message}</Typography>
                            </Box>
                        ))}
                        {recent.length === 0 && (
                            <Typography color="text.secondary">No broadcasts yet.</Typography>
                        )}
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Broadcasts;
